import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './animate-ui/components/buttons/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AlertCircle, Shield } from 'lucide-react';
import { FcGoogle } from "react-icons/fc";
import { BsMicrosoft } from "react-icons/bs";
import { useTranslation } from '../i18n';
import { MSAL_ENABLED } from 'src/lib/msalClient';
import { HiOutlineSparkles } from 'react-icons/hi2';

export const LoginPage: React.FC = () => {
  const { login, error, isLoading, loginWithMicrosoft } = useAuth();

  const handleManualLogin = () => {
    login();
  };

  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background app-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('login.loading_auth', 'Loading authentication...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background app-bg flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="card-accent shadow-lg">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <img src="svgviewer-output.svg" alt="ICD Icon" className="h-16 w-16" />
            </div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-primary to-medical-600 bg-clip-text text-transparent flex items-center justify-center gap-1">
              {t('login.title', 'ICD-10 Medical Codes')}
              <HiOutlineSparkles className="text-2xl fill-medical-600 stroke-medical-600" />
            </CardTitle>
            <p className="text-muted-foreground">
              {t('login.subtitle', 'Sign in to access the medical code extraction system')}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="flex items-center space-x-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md border border-destructive/20"
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <p>{error}</p>
              </motion.div>
            )}

            <div className="space-y-4">
              <Button
                onClick={handleManualLogin}
                variant="outline"
                className="w-full justify-start pl-4"
                disabled={isLoading}
              >
                <FcGoogle className='mr-2 w-8 h-6' /> {isLoading ? t('login.loading', 'Loading...') : t('login.sign_in_google', 'Sign in with Google')}
              </Button>
              <Button
                onClick={() => loginWithMicrosoft()}
                variant="outline"
                className="w-full text-left"
                disabled={isLoading || MSAL_ENABLED === false}
              >
                <BsMicrosoft className='mr-2 w-8 h-6' /> {isLoading ? t('login.loading', 'Loading...') : t('login.sign_in_microsoft', 'Sign in with Microsoft')}
              </Button>
            </div>

            <div className="flex items-center space-x-2 text-xs text-muted-foreground bg-muted/30 p-3 rounded-md">
              <Shield className="h-4 w-4 flex-shrink-0 stroke-primary" />
              <p>
                {t('login.access_restricted', 'Access is restricted to authorized users only.')} {t('login.contact_admin', 'Contact your administrator if you need access.')}
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default LoginPage;